/**
 * ViewR 작업 관련 API
 */

import { apiCall, API_BASE_URL } from "./base";
import type { ViewRData } from "../../types/unity";

/**
 * [레거시] .ViewR 생성/수정 작업을 /unity/dispatch-job 또는 /unity/create-viewr 로 요청.
 * Create ViewR 은 createViewR() 및 POST /api/jobs/viewr/create 사용 권장.
 */
export const dispatchViewRJob = async (
  action: string,
  payload: unknown,
  userToken: string | null
): Promise<unknown> => {
  // 백엔드 유효성: action만 허용될 수 있음
  const normalizedAction =
    action === "create_from_scratch" ? "create" : action;
  const requestBody =
    payload == null
      ? { action: normalizedAction }
      : { action: normalizedAction, payload };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(requestBody),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  // HTTP API 기반 dispatch-job 엔드포인트를 호출
  try {
    return await apiCall("/unity/dispatch-job", options);
  } catch (err) {
    const error = err as Error;
    const message = String(error?.message || "");
    // 404/422/500 등 실패 시 레거시 create-viewr 엔드포인트를 폴백 시도
    if (
      message.includes("404") ||
      message.includes("500") ||
      message.includes("422")
    ) {
      console.warn(
        "/unity/dispatch-job 실패, /unity/create-viewr 폴백 시도",
        message
      );
      const mappedAction = normalizedAction;
      const fallbackOptions: RequestInit = {
        method: "POST",
        body: JSON.stringify({ action: mappedAction }), // payload 제거
        headers: {
          Authorization: userToken ? `Bearer ${userToken}` : "",
          "Content-Type": "application/json",
        },
      };
      return await apiCall("/unity/create-viewr", fallbackOptions);
    }
    throw err;
  }
};

export interface FinalViewRDataRequest {
  gcsPath?: string | null;
  projectId?: string | null;
}

export interface FinalViewRDataResponse {
  status?: "success" | "fallback_required" | string;
  viewRData?: ViewRData;
  viewrGCSPath?: string;
  hash?: string;
  source?: string;
  requires_legacy_bootstrap?: boolean;
  project_id?: number;
  storage_mode?: "legacy" | "revision";
  edit_head_revision_id?: string | null;
}

export const fetchFinalViewRData = async (
  request: FinalViewRDataRequest,
  userToken: string | null,
): Promise<FinalViewRDataResponse> => {
  const payload: Record<string, string> = {};
  if (request.gcsPath) payload.gcs_path = request.gcsPath;
  if (request.projectId) payload.project_id = request.projectId;
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  try {
    return (await apiCall("/final-files/viewr-data", options)) as FinalViewRDataResponse;
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return (await apiCall(
        "/api/storage/final-files/viewr-data",
        options,
      )) as FinalViewRDataResponse;
    }
    throw err;
  }
};

export interface RevisionProjectCreateResult {
  status: "completed";
  project_id: number;
  display_name: string;
  gcs_path: string;
  storage_mode: "revision";
  edit_head_revision_id: string;
  revision_id: string;
  created_at: string;
}

export const isRevisionProjectCreateResult = (
  value: unknown,
): value is RevisionProjectCreateResult => {
  const result = value as Partial<RevisionProjectCreateResult> | null;
  return Boolean(
    result &&
      result.storage_mode === "revision" &&
      result.project_id &&
      result.edit_head_revision_id &&
      result.gcs_path,
  );
};

/**
 * Create from scratch returns an initial Revision project immediately.
 * Explicit file paths retain the legacy Converter job response.
 */
export const createViewR = async (
  userToken: string | null,
  filePath: string = ""
): Promise<unknown> => {
  const payload = filePath ? { file_path: filePath } : { file_path: "" };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  return await apiCall("/api/jobs/viewr/create", options);
};

/** 백엔드 `sort`: last_viewed=최근 활동 시각 최신순, date_created=생성일 오래된 순 */
export type FinalFilesListSort = "last_viewed" | "date_created";

/**
 * ViewR 파일 목록 조회
 */
export const listFinalFiles = async (
  userToken: string | null,
  sort: FinalFilesListSort = "last_viewed"
): Promise<unknown> => {
  const q = new URLSearchParams({ sort });
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  try {
    return await apiCall(`/final-files/list?${q.toString()}`, options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall(
        `/api/storage/final-files/list?${q.toString()}`,
        options
      );
    }
    throw err;
  }
};

/**
 * 프로젝트 열람 기록 → 서버 last_viewed_at 갱신 (계정 단위 공통 순서)
 */
export const recordFinalFileView = async (
  userToken: string | null,
  body: { projectId?: string; gcsPath?: string; fullPath?: string }
): Promise<unknown> => {
  const payload: Record<string, string> = {};
  if (body.projectId) payload.project_id = body.projectId;
  if (body.gcsPath) payload.gcs_path = body.gcsPath;
  if (body.fullPath) payload.full_path = body.fullPath;
  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  };
  try {
    return await apiCall("/final-files/record-view", options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall("/api/storage/final-files/record-view", options);
    }
    throw err;
  }
};

/**
 * 최종 .viewr 북마크 — viewr_projects 동기화 (계정·기기 공통)
 */
export const setFinalFileBookmark = async (
  userToken: string | null,
  body: {
    bookmarked: boolean;
    project_id?: string;
    gcs_path?: string;
    full_path?: string;
  }
): Promise<unknown> => {
  const payload: Record<string, string | boolean> = {
    bookmarked: body.bookmarked,
  };
  if (body.project_id) payload.project_id = body.project_id;
  if (body.gcs_path) payload.gcs_path = body.gcs_path;
  if (body.full_path) payload.full_path = body.full_path;
  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  };
  try {
    return await apiCall("/final-files/set-bookmark", options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall("/api/storage/final-files/set-bookmark", options);
    }
    throw err;
  }
};

/**
 * 샘플 ViewR 파일 목록 조회
 */
export const listSampleFinalFiles = async (
  userToken: string | null,
  signal?: AbortSignal,
): Promise<unknown> => {
  const options: RequestInit = {
    method: "GET",
    signal,
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  try {
    return await apiCall("/final-files/sample-list", options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall("/api/storage/final-files/sample-list", options);
    }
    throw err;
  }
};

/**
 * ViewR_Samples 하위 샘플 목록 (convert 버킷 기반)
 */
export const listSampleConvertFiles = async (
  userToken: string | null
): Promise<unknown> => {
  const options: RequestInit = {
    method: "GET",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
    },
  };
  return await apiCall("/convert-files/sample-list", options);
};

/**
 * ViewR 파일 이름 변경
 * @param filePath - gcs 경로 (projectId 없을 때 사용)
 * @param newName - 새 표시명
 * @param userToken - 인증 토큰
 * @param projectId - viewr_projects.id (우선 사용, 있으면 filePath 대신 전달)
 */
export const renameFinalFile = async (
  filePath: string,
  newName: string,
  userToken: string | null,
  projectId?: string
): Promise<unknown> => {
  const payload = projectId
    ? { project_id: projectId, new_name: newName }
    : { file_path: filePath, new_name: newName };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  try {
    return await apiCall("/final-files/rename", options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall("/api/storage/final-files/rename", options);
    }
    throw err;
  }
};

/**
 * ViewR 파일 복제
 * @param filePath - gcs 경로 (projectId 없을 때 사용)
 * @param userToken - 인증 토큰
 * @param projectId - viewr_projects.id (우선 사용)
 */
export const duplicateFinalFile = async (
  filePath: string,
  userToken: string | null,
  projectId?: string,
  idempotencyKey?: string,
): Promise<unknown> => {
  const payload = {
    ...(projectId ? { project_id: projectId } : { file_path: filePath }),
    ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
  };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  return await apiCall("/api/storage/final-files/duplicate", options);
};

/**
 * ViewR 파일 삭제 (휴지통으로 이동).
 * DELETE + body가 일부 환경에서 404가 나므로 POST /final-files/move-to-trash 사용.
 * @param filePath - gcs 경로 (projectId 없을 때 사용)
 * @param userToken - 인증 토큰
 * @param projectId - viewr_projects.id (우선 사용)
 */
export const deleteFinalFile = async (
  filePath: string,
  userToken: string | null,
  projectId?: string
): Promise<unknown> => {
  const payload = projectId ? { project_id: projectId } : { file_path: filePath };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };
  try {
    return await apiCall("/final-files/move-to-trash", options);
  } catch (err) {
    const error = err as Error;
    if (typeof error?.message === "string" && error.message.includes("404")) {
      return await apiCall("/api/storage/final-files/move-to-trash", options);
    }
    throw err;
  }
};

/**
 * ViewR 파일 다운로드 URL 생성
 */
export const getFinalDownloadUrl = async (
  filePath: string,
  userToken: string | null
): Promise<unknown> => {
  const payload = {
    file_path: filePath,
  };
  const options: RequestInit = {
    method: "POST",
    body: JSON.stringify(payload),
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };

  return await apiCall("/final-files/generate-download-url", options);
};

/**
 * ViewR 파일 업로드용 서명된 URL 요청 (final-files 버킷) - 레거시
 */
export const getFinalFilesSignedUploadUrl = async (
  filename: string,
  contentType: string,
  userToken: string | null
): Promise<unknown> => {
  // 서버가 Query Parameter를 요구하므로 URL에 파라미터 추가
  const queryParams = new URLSearchParams({
    filename: filename,
    content_type: contentType,
  });

  const url = `${API_BASE_URL}/final-files/generate-signed-url?${queryParams.toString()}`;

  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      "Content-Type": "application/json",
    },
  };

  // apiCall은 기본적으로 JSON을 보내므로 직접 fetch 사용
  const response = await fetch(url, options);

  if (!response.ok) {
    const errorText = await response.text();
    console.error(
      `❌ [apiService] Signed URL Request Failed: ${response.status}`,
      errorText
    );
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`
    );
  }

  const data = await response.json();
  return data;
};

/**
 * ViewR 파일 직접 업로드 (final-files 버킷)
 */
export const uploadFinalFile = async (
  file: File,
  userToken: string | null,
  signal?: AbortSignal
): Promise<unknown> => {
  const formData = new FormData();
  formData.append("file", file);

  const options: RequestInit = {
    method: "POST",
    headers: {
      Authorization: userToken ? `Bearer ${userToken}` : "",
      // Content-Type 헤더를 명시적으로 설정하지 않음 (FormData가 자동으로 설정)
    },
    body: formData,
    signal,
  };

  // apiCall은 기본적으로 JSON을 보내므로 직접 fetch 사용
  const url = `${API_BASE_URL}/final-files/upload`;
  const response = await fetch(url, options);

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP error! status: ${response.status}, message: ${errorText}`
    );
  }

  return await response.json();
};
